import { Box, Dropdown, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useState } from "react";

import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { changeLanguage } from "~/i18n";
import { ArrowDown, Globe } from "~/icons";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type LocaleSelectProps = {
  languages: GetSettingsQuerySdk["languages"];
  availableLanguages: Array<string>;
  language: string;
  variant?: "static" | "positioned";
  offset?: number;
  isNonModal?: boolean;
};

function isLanguageCode(
  code: string | null | undefined,
  availableLanguages: Array<string>,
): code is LanguageCode {
  if (code) {
    return availableLanguages.includes(code);
  }
  return false;
}

export const LocaleSelect: FC<LocaleSelectProps> = ({
  languages,
  language,
  availableLanguages,
  variant = "positioned",
  offset,
  isNonModal = true,
}) => {
  const { getDirectusImage } = useDirectus();
  const [isActive, setIsActive] = useState(false);
  const selectedLang = languages?.find((item) => {
    if (item?.code === language) {
      return item;
    }

    return undefined;
  });

  return (
    <Dropdown.Trigger
      onOpenChange={(isOpen) => setIsActive(isOpen)}
      onAction={(key) => {
        const index = Number(key);

        if (
          Number.isInteger(index) &&
          index < languages.length &&
          languages[index].code !== language &&
          isLanguageCode(languages[index].code, availableLanguages)
        ) {
          changeLanguage(languages[index].code);
        }
      }}
      strategy="mixed"
      isNonModal={isNonModal}
      placement="bottom right"
      offset={offset}
      variant={variant}
      triggerElement={
        <Box
          width="100%"
          className="cursor-pointer "
          data-testid="header-locale-switch"
        >
          <Flex
            align="center"
            justify="center"
            p={{ py: 8 }}
            color="text-primary-default"
            hover={{ color: "text-primary-hovered" }}
            className={` hover:stroke-[var(--colors-text-primary-hovered)]  ${isActive && "stroke-[var(--colors-text-selection)]"}`}
          >
            <Globe />
            <Typography
              size="medium"
              weight="semibold"
              textTransform="uppercase"
              p={{ pr: 8, pl: 4 }}
            >
              {selectedLang ? selectedLang?.code : "Pick"}
            </Typography>
            <ArrowDown
              className={`${isActive && "transform rotate-[-90deg]"}`}
            />
          </Flex>
        </Box>
      }
      dropdownWrapper={(wrapperProps) => (
        <Box
          p={2}
          border={{ radius: "8px" }}
          md={{
            width: 200,
            p: 8,
            bg: "background-secondary-transparent",
          }}
          backdropBlur={10}
        >
          {wrapperProps.children}
        </Box>
      )}
    >
      {languages?.map(
        (item, index) =>
          item && (
            <Dropdown.Item key={index} textValue={item?.code}>
              <Flex
                align="center"
                justify="center"
                gap={16}
                key={index}
                p={{ py: 12 }}
                className="cursor-pointer"
                md={{
                  justify: "flex-start",
                  p: { px: 16 },
                  hover: { bg: "surface-primary-subtle" },
                  border: { radius: "8px" },
                }}
                textTransform="uppercase"
                onClick={() => {
                  if (
                    languages[index].code !== language &&
                    isLanguageCode(languages[index].code, availableLanguages)
                  ) {
                    changeLanguage(languages[index].code);
                  }
                }}
              >
                {item?.icon?.id && (
                  <Image
                    value={getDirectusImage(item.icon.id ?? "", {
                      format: "webp",
                      width: 24,
                    })}
                  />
                )}
                <Typography size="medium" textTransform="uppercase">
                  {item?.code}
                </Typography>
              </Flex>
            </Dropdown.Item>
          ),
      )}
    </Dropdown.Trigger>
  );
};
